import { Inject, Injectable, DOCUMENT } from '@angular/core';
import tinycolor from 'tinycolor2';
import { RegistrationService } from './registration.service';
import { Provider } from '../models/provider.model';
import { ProviderService } from './provider.service';

const THEME_PROPERTY_GROUPS = {
  whiteLabel: [
    '--white-label-black', // Always 'black'. Text on light backgrounds in white-label mode.
    '--white-label-white'  // Always 'white'. Text/icons on dark backgrounds in white-label mode.
  ],
  header: [
    '--custom-header-color',         // Header background color (provider.customHeaderColor).
    '--contrast-custom-header-color' // 'black' or 'white' based on header color contrast. Text/icons inside the header.
  ],
  button: [
    '--custom-button-color',           // Base button color (provider.customButtonColor). Backgrounds, borders, surfaces.
    '--contrast-custom-button-color',  // 'black' if btnColor is light, 'white' if dark. Text/icons ON the button color.
    '--primary-on-dark',               // 'white' if btnColor is dark, btnColor if light. Primary text on dark backgrounds.
    '--primary-base-color',
    '--primary-base-color-20',
    '--primary-base-color-30',
    '--primary-base-color-50',
    '--primary-lighter-color',
    '--primary-darker-color',
    '--accent-base-color',
    '--accent-base-color-50',
    '--accent-lighter-color',
    '--accent-darker-color',
  ],
} as const;

type ThemePropertyGroup = keyof typeof THEME_PROPERTY_GROUPS;
type ThemeTokenMap = Record<string, string>;

@Injectable({
  providedIn: 'root',
})
export class ProviderCustomizeService {

  private themeApplied = false;
  private readonly themePropertyGroups = THEME_PROPERTY_GROUPS;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private registrationService: RegistrationService,
    private providerService: ProviderService
  ) {}

  setThemeColor(): void {
    const provider = this.getProvider();

    if(!provider?.whiteLabel || this.providerService.isLanus(provider?.id)) return;

    this.themeApplied = true;
    const btnColor = provider?.customButtonColor;
    const headerColor = provider?.customHeaderColor;

    this.applyThemeTokens(this.getWhiteLabelTokens());

    if(headerColor) {
      this.applyThemeTokens(this.getHeaderThemeTokens(headerColor));
    } else {
      this.removeThemeGroups('header');
    }

    if(btnColor) {
      this.applyThemeTokens(this.getButtonThemeTokens(btnColor));
    } else {
      this.removeThemeGroups('button');
    }
  }

  removeThemeColor(): void {
    if(!this.themeApplied) return;
    this.themeApplied = false;

    const provider = this.getProvider();

    if(!provider?.whiteLabel) {
      this.removeThemeGroups('whiteLabel', 'header', 'button');
      return;
    }

    if(!provider?.customHeaderColor) this.removeThemeGroups('header');
    if(!provider?.customButtonColor) this.removeThemeGroups('button');
  }

  getContrastColor(bgColor?: string): string | null{
    const provider = this.getProvider();
    if (!this.hasCustomStyles(provider)) return null;

    return tinycolor(bgColor || provider.customButtonColor).isLight() ? 'black' : 'white';
  }

  // Searchs the DOM from the given element until it finds an opaque background and determines whether it is dark.
  isElementOnDarkBackground(element: HTMLElement | null): boolean {
    const transparent = new Set(['transparent', 'rgba(0, 0, 0, 0)']);
    let el = element;
    let depth = 0;
    while (el && depth < 20) {
      const bg = getComputedStyle(el).backgroundColor;
      if (!transparent.has(bg)) {
        return !tinycolor(bg).isLight();
      }
      el = el.parentElement;
      depth++;
    }
    return false;
  }

  private getWhiteLabelTokens(): ThemeTokenMap {
    return {
      '--white-label-black': 'black',
      '--white-label-white': 'white',
    };
  }

  private getHeaderThemeTokens(headerColor: string): ThemeTokenMap {
    return {
      '--custom-header-color': headerColor,
      '--contrast-custom-header-color': this.getContrastColor(headerColor) as string,
    };
  }

  private getButtonThemeTokens(btnColor: string): ThemeTokenMap {
    const buttonColor = tinycolor(btnColor);
    const btnLighterColor = buttonColor.clone().lighten(5).toHexString();
    const btnDarkerColor = buttonColor.clone().darken(5).toHexString();
    const contrastColor = this.getContrastColor(btnColor) as string;

    return {
      '--custom-button-color': btnColor,
      '--contrast-custom-button-color': contrastColor,
      '--primary-on-dark': contrastColor === 'white' ? 'white' : btnColor,
      '--primary-base-color': btnColor,
      '--primary-base-color-20': buttonColor.clone().setAlpha(0.2).toRgbString(),
      '--primary-base-color-30': buttonColor.clone().setAlpha(0.3).toRgbString(),
      '--primary-base-color-50': buttonColor.clone().setAlpha(0.5).toRgbString(),
      '--primary-lighter-color': btnLighterColor,
      '--primary-darker-color': btnDarkerColor,
      '--accent-base-color': btnColor,
      '--accent-base-color-50': buttonColor.clone().setAlpha(0.5).toRgbString(),
      '--accent-lighter-color': btnLighterColor,
      '--accent-darker-color': btnDarkerColor,
    };
  }

  private applyThemeTokens(tokens: ThemeTokenMap): void {
    Object.entries(tokens).forEach(([property, value]) =>
      this.setThemeProperty(property, value)
    );
  }

  private removeThemeGroups(...groups: ThemePropertyGroup[]): void {
    const properties = groups.reduce<string[]>((styles, group) => {
      styles.push(...this.themePropertyGroups[group]);
      return styles;
    }, []);

    this.removeStyles(properties);
  }

  private removeStyles(properties: readonly string[]): void {
    properties.forEach((property) =>
      this.document.documentElement.style.removeProperty(property)
    );
  }

  private setThemeProperty(property: string, value: string): void {
    this.document.documentElement.style.setProperty(property, value, 'important');
  }

  private getProvider(): Provider {
    return this.registrationService.getProvider();
  }

  private hasCustomStyles(provider: Provider): boolean {
    return !!(provider?.whiteLabel && provider?.customButtonColor);
  }

}
